import ExcelJS from "exceljs";
import FileSaver from "file-saver";

export interface ExcelSheet {
  name: string;
  data: Record<string, string | number | boolean | null | undefined>[];
}

/**
 * Shared utility for exporting data to .xlsx files using ExcelJS (MIT licensed).
 * Replaces all previous usage of SheetJS (xlsx) throughout the codebase.
 */
export async function exportToExcel(sheets: ExcelSheet[], filename: string): Promise<void> {
  const workbook = new ExcelJS.Workbook();
  workbook.created = new Date();

  for (const sheet of sheets) {
    if (sheet.data.length === 0) continue;

    const worksheet = workbook.addWorksheet(sheet.name);

    // Derive columns from the first row's keys
    const columns = Object.keys(sheet.data[0]).map((key) => ({
      header: key,
      key,
      width: Math.max(key.length + 4, 14),
    }));
    worksheet.columns = columns;

    // Style header row
    worksheet.getRow(1).font = { bold: true };
    worksheet.getRow(1).fill = {
      type: "pattern" as const,
      pattern: "solid" as const,
      fgColor: { argb: "FFE8E8E8" },
    };

    // Add data rows
    for (const row of sheet.data) {
      worksheet.addRow(row);
    }

    // Auto-fit column widths based on content (capped at 40)
    worksheet.columns.forEach((col) => {
      let maxLen = col.header ? col.header.length : 10;
      col.eachCell?.({ includeEmpty: false }, (cell) => {
        const cellLen = cell.value ? String(cell.value).length : 0;
        if (cellLen > maxLen) maxLen = cellLen;
      });
      col.width = Math.min(maxLen + 2, 40);
    });
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  FileSaver.saveAs(blob, filename.endsWith(".xlsx") ? filename : `${filename}.xlsx`);
}
